import { useState } from 'react';
import { Link } from 'react-router-dom';
import PageTransition from '../components/PageTransition';
import ScrollReveal from '../components/ScrollReveal';
import { blogPosts, blogCategories, getFeaturedPost } from '../data/blog';
import { useSEO } from '../hooks/useSEO';
import { PAGE_SEO } from '../utils/seo';

const categoryPillColors: Record<string, string> = {
  Engineering: 'pill--green',
  Design: 'pill--orange',
  Philosophy: 'pill--blue',
};

export default function JournalPage() {
  const [activeCategory, setActiveCategory] = useState('All');
  const featured = getFeaturedPost();

  // Apply SEO
  useSEO(PAGE_SEO.journal);

  const filteredPosts =
    activeCategory === 'All'
      ? blogPosts.filter(p => !p.featured)
      : blogPosts.filter(p => p.category === activeCategory && !p.featured);

  return (
    <PageTransition>
      {/* Hero */}
      <section className="section">
        <div className="container">
          <ScrollReveal>
            <h1 className="h-1" style={{ marginBottom: 'var(--space-4)' }}>
              Journal
            </h1>
            <p className="body-lg" style={{ color: 'var(--text-secondary)', maxWidth: '640px' }}>
              Deep dives into our craft, process, and the thinking behind the work.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Featured Post */}
      {featured && (
        <ScrollReveal>
          <section style={{ padding: '0 0 var(--space-8)' }}>
            <div className="container">
              <span className="caption-bold" style={{ color: 'var(--accent-green)', display: 'block', marginBottom: 'var(--space-4)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Featured
              </span>
              <Link
                to={`/journal/${featured.slug}`}
                className="card featured-post-card"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 'var(--space-8)',
                  padding: 'var(--space-8)',
                  textDecoration: 'none',
                  color: 'inherit',
                  overflow: 'hidden',
                }}
              >
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
                    <span className={`pill ${categoryPillColors[featured.category] || ''}`}>
                      {featured.category}
                    </span>
                    <time className="small" style={{ color: 'var(--text-secondary)' }}>
                      {new Date(featured.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </time>
                  </div>
                  <h2 className="h-2" style={{ marginBottom: 'var(--space-4)' }}>
                    {featured.title}
                  </h2>
                  <p className="body" style={{ color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: 'var(--space-6)' }}>
                    {featured.excerpt}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <span className="btn btn--primary btn--small">
                      Read Article
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </span>
                    <span className="caption" style={{ color: 'var(--text-secondary)' }}>
                      {featured.readTime}
                    </span>
                  </div>
                </div>
                <div
                  className="featured-post-hero"
                  style={{
                    background: featured.gradient,
                    borderRadius: 'var(--radius-comfortable)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '280px',
                  }}
                >
                  <span style={{ fontFamily: 'var(--font-title)', fontSize: '5rem', fontWeight: 800, color: 'rgba(255,255,255,0.3)', lineHeight: 1 }}>
                    {featured.letter}
                  </span>
                </div>
              </Link>
            </div>
          </section>
        </ScrollReveal>
      )}

      {/* Category Filters */}
      <ScrollReveal>
        <div className="container">
          <div className="filter-pills" role="tablist" aria-label="Filter journal posts by category">
            {blogCategories.map((cat) => (
              <button
                key={cat}
                role="tab"
                aria-selected={activeCategory === cat}
                className={`filter-pill pill${activeCategory === cat ? ' filter-pill--active' : ''}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </ScrollReveal>

      {/* Blog Posts Grid */}
      <ScrollReveal>
        <section className="section">
          <div className="container">
            {filteredPosts.length > 0 ? (
              <div className="grid-2">
                {filteredPosts.map((post, i) => (
                  <ScrollReveal key={post.id} delay={i * 80}>
                    <article className="journal-card card" style={{ padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
                      <div className="journal-card__image" style={{ position: 'relative', overflow: 'hidden' }}>
                        <div
                          style={{
                            background: post.gradient,
                            aspectRatio: '16 / 9',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <span style={{ fontFamily: 'var(--font-title)', fontSize: '3rem', fontWeight: 800, color: 'rgba(255,255,255,0.25)', lineHeight: 1 }}>
                            {post.letter}
                          </span>
                        </div>
                      </div>
                      <div className="journal-card__content" style={{ padding: 'var(--space-5)', flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-3)', flexWrap: 'wrap' }}>
                          <time className="small" style={{ color: 'var(--text-secondary)' }}>
                            {new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                          </time>
                          <span className={`pill ${categoryPillColors[post.category] || ''}`}>
                            {post.category}
                          </span>
                        </div>
                        <h2 className="h-3" style={{ marginBottom: 'var(--space-3)' }}>
                          {post.title}
                        </h2>
                        <p className="caption" style={{ color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 'var(--space-4)', flex: 1 }}>
                          {post.excerpt}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span className="small" style={{ color: 'var(--text-secondary)' }}>
                            {post.readTime}
                          </span>
                          <Link
                            to={`/journal/${post.slug}`}
                            className="btn btn--ghost btn--small"
                            style={{ fontSize: '0.75rem' }}
                          >
                            Read More
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="5" y1="12" x2="19" y2="12" />
                              <polyline points="12 5 19 12 12 19" />
                            </svg>
                          </Link>
                        </div>
                      </div>
                    </article>
                  </ScrollReveal>
                ))}
              </div>
            ) : (
              <div className="empty-state" style={{ marginTop: 'var(--space-12)', textAlign: 'center' }}>
                <p className="body" style={{ color: 'var(--text-secondary)' }}>
                  No posts in this category yet.
                </p>
              </div>
            )}
          </div>
        </section>
      </ScrollReveal>
    </PageTransition>
  );
}
